import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { EnvelopeSimple, CheckCircle, Users, Sparkle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AnimatedCounter from './AnimatedCounter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WaitlistSection({ isDark = true }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [position, setPosition] = useState(null);
  const [waitlistCount, setWaitlistCount] = useState(0);
  
  useEffect(() => {
    // Charger le nombre d'inscrits
    const fetchCount = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/public/waitlist/count`);
        setWaitlistCount(response.data.count);
      } catch (error) {
        console.error('Error fetching waitlist count:', error);
      }
    };
    fetchCount();
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/public/waitlist`, { email });
      
      if (response.data.success) {
        setSubmitted(true);
        setPosition(response.data.position);
        setWaitlistCount(prev => response.data.already_registered ? prev : prev + 1);
        
        if (response.data.already_registered) {
          toast.info(response.data.message);
        } else {
          toast.success(response.data.message);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'inscription';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <section className="py-20 sm:py-32 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-violet-500/5 via-transparent to-pink-500/5" />
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center"
        >
          {/* Badge Beta */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/20 to-pink-500/20 border border-violet-500/30 mb-6"
          >
            <Sparkle size={16} weight="fill" className="text-violet-400" />
            <span className="text-sm font-medium text-white/90">Bêta — Accès gratuit limité</span>
          </motion.div>
          
          {/* Title */}
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Rejoignez les{' '}
            <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
              premiers utilisateurs
            </span>
          </h2>
          
          <p className={`text-lg sm:text-xl mb-8 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
            Soyez parmi les premiers à automatiser votre acquisition client avec l'IA.
            <br />
            <span className="text-violet-400 font-medium">Places limitées en accès anticipé.</span>
          </p>
          
          {/* Counter */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-2 mb-8"
          >
            <Users size={24} weight="duotone" className="text-violet-400" />
            <span className={`text-lg ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              Déjà{' '}
              <AnimatedCounter 
                value={waitlistCount + 247} 
                className="font-bold text-violet-400"
              />
              {' '}personnes inscrites
            </span>
          </motion.div>
          
          {/* Form or Success Message */}
          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              >
                <div className="relative flex-1">
                  <EnvelopeSimple 
                    size={20} 
                    className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/40' : 'text-gray-400'}`}
                  />
                  <Input
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={`pl-12 h-14 text-base ${
                      isDark 
                        ? 'bg-white/5 border-white/10 focus:border-violet-500/50' 
                        : 'bg-white border-gray-200 focus:border-violet-500'
                    }`}
                    data-testid="waitlist-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="h-14 px-8 bg-gradient-to-r from-violet-500 to-pink-500 hover:opacity-90 text-base font-semibold whitespace-nowrap"
                  data-testid="waitlist-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                      />
                      Inscription...
                    </span>
                  ) : (
                    'Rejoindre les premiers'
                  )}
                </Button>
              </motion.form>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`p-6 rounded-2xl ${
                  isDark 
                    ? 'bg-emerald-500/10 border border-emerald-500/30' 
                    : 'bg-emerald-50 border border-emerald-200'
                }`}
              >
                <CheckCircle size={48} weight="duotone" className="text-emerald-400 mx-auto mb-4" />
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Vous êtes inscrit(e) ! 🎉
                </h3>
                <p className={isDark ? 'text-white/60' : 'text-gray-600'}>
                  Vous êtes en position <span className="font-bold text-violet-400">#{position || waitlistCount}</span> sur la liste.
                  <br />
                  Nous vous contacterons très bientôt pour votre accès anticipé.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Trust indicators */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className={`mt-6 text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}
          >
            🔒 Pas de spam. Vos données restent confidentielles.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}
